import cn from 'classnames';
import React, { PropsWithChildren } from 'react';
import { ElementOrArrayOfElement } from '../common/Types';
import { Modal, ModalBody, ModalTitle } from '../modal';
import { ButtonAction, ButtonType, PageState } from './types';

interface WizardProps {
  visible: boolean;
  size?: 'md' | 'lg' | 'xl';
  disabledCount?: number;
  reCalculate?: boolean;
  closable?: boolean;
  currentStep: number; // 0 indexed
  title: string | JSX.Element;
  pages: React.ReactElement<PagesProps>;
  buttons: React.ReactElement<ButtonsProps>;
  onStepChange?: (step: number) => void;
  onCloseClick: () => void;
  onBackdropClick?: () => void;
}

export const Wizard = React.forwardRef<HTMLDivElement, WizardProps>(
  (props, ref) => {
    const {
      visible,
      size,
      disabledCount,
      reCalculate = false,
      closable,
      currentStep,
      title,
      pages,
      buttons,
      onStepChange,
      onCloseClick,
      onBackdropClick
    } = props;

    const filteredPages = Array.isArray(pages.props.children)
      ? (pages.props.children.filter(
        c => c !== null && c !== undefined
      ) as Array<React.ReactElement<PageProps>>)
      : pages.props.children !== null && pages.props.children !== undefined
        ? [pages.props.children]
        : [];
    const onClickNav = (step: number) => {
      if (onStepChange) {
        onStepChange(step);
      }
    };

    const renderWizardPages = () => {
      const steps = filteredPages;
      return React.Children.map(steps, (step, index) => {
        return React.cloneElement(step, {
          active: index === currentStep
        });
      });
    };

    const renderModalNav = () => (
      <nav className="modal-nav clr-wizard-stepnav-wrapper">
        <h3 className="clr-wizard-title">{title}</h3>
        <WizardStepNav
          currentStep={currentStep}
          pages={filteredPages}
          onClick={onClickNav}
        />
      </nav>
    );

    const renderModalTitle = () => {
      const currentPage = filteredPages[currentStep];
      return (
        <ModalTitle>
          <span className="modal-title-text">{currentPage.props.title}</span>
        </ModalTitle>
      );
    };

    const renderModalBody = () => {
      return (
        <ModalBody>
          <main className="clr-wizard-content">{renderWizardPages()}</main>
        </ModalBody>
      );
    };

    const renderModalFooter = () => (
      <nav className="modal-footer clr-wizard-footer">
        <div className="clr-wizard-footer-buttons">
          <div className="clr-wizard-footer-buttons-wrapper">
            {buttons.props.children}
          </div>
        </div>
      </nav>
    );

    return (
      <div ref={ref} className="clr-wizard">
        <Modal
          visible={visible}
          size={size}
          disabledCount={disabledCount}
          reCalculate={reCalculate}
          closable={closable}
          title={renderModalTitle()}
          nav={renderModalNav()}
          body={renderModalBody()}
          footer={renderModalFooter()}
          onCloseClick={onCloseClick}
          onBackdropClick={onBackdropClick}
        />
      </div>
    );
  }
);

interface PagesProps {
  children: ElementOrArrayOfElement<PageProps>;
}

export const WizardPages: React.FC<PagesProps> = props => <>{props.children}</>;

type PageProps = PropsWithChildren<{
  title: string;
  navTitle?: string;
  active?: boolean;
  state?: PageState;
}>;

export const WizardPage = React.forwardRef<HTMLDivElement, PageProps>(
  (props, ref) => {
    const { active, children } = props;
    return (
      <div
        ref={ref}
        className={cn('clr-wizard-page', { active })}
        aria-hidden={!active}>
        {children}
      </div>
    );
  }
);

interface ButtonsProps {
  children: ElementOrArrayOfElement<ButtonProps>;
}

type ButtonProps = PropsWithChildren<{
  action: ButtonAction;
  type?: ButtonType;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}>;

export const WizardButtons: React.FC<ButtonsProps> = props => (
  <>{props.children}</>
);

export const WizardButton = React.forwardRef<HTMLDivElement, ButtonProps>(
  (props, ref) => {
    const { action, type = 'button', children, onClick, disabled } = props;

    const className = cn('btn clr-wizard-btn', {
      'btn-link clr-wizard-btn--tertiary': action === 'cancel',
      'btn-outline clr-wizard-btn--secondary': action === 'previous',
      'btn-primary clr-wizard-btn--primary': action === 'next',
      'btn-success': action === 'finish'
    });
    return (
      <div ref={ref} className="clr-wizard-btn-wrapper">
        <button
          type={type}
          className={className}
          onClick={onClick}
          disabled={disabled}>
          {children}
        </button>
      </div>
    );
  }
);

interface StepNavProps {
  pages: Array<React.ReactElement<PageProps>>;
  currentStep: number;
  onClick: (index: number) => void;
}

const WizardStepNav = React.forwardRef<HTMLDivElement, StepNavProps>(
  (props, ref) => {
    const { currentStep, pages, onClick } = props;

    const renderSteps = () => {
      return pages.map((step, index) => {
        const onClickNavItem = () =>
          step.props.state === 'complete' && onClick(index);
        const activeStep = currentStep === index;
        return (
          <li
            key={index}
            className={cn('clr-wizard-stepnav-item', step.props.state, {
              active: activeStep
            })}>
            <button
              className="btn btn-link clr-wizard-stepnav-link"
              type="button"
              disabled={!activeStep && step.props.state !== 'complete'}
              onClick={onClickNavItem}>
              {step.props.navTitle || step.props.title}
            </button>
          </li>
        );
      });
    };

    return (
      <div ref={ref} className="clr-wizard-stepnav">
        <ol className="clr-wizard-stepnav-list" role="tablist">
          {renderSteps()}
        </ol>
      </div>
    );
  }
);
